import React, { ButtonHTMLAttributes, ReactNode, useCallback, useRef, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { trackButtonClick } from '../../firebaseConfig';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'danger';
  children: ReactNode;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  // Analytics props
  trackingId?: string;
  trackingLocation?: string;
  trackingDisabled?: boolean;
}

export function Button({
  variant = 'primary',
  children,
  leftIcon,
  rightIcon,
  fullWidth = false,
  size = 'md',
  loading = false,
  disabled = false,
  className = '',
  // Analytics props
  trackingId,
  trackingLocation,
  trackingDisabled = false,
  onClick,
  ...rest
}: ButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const loaderRef = useRef<HTMLDivElement>(null);
  
  const sizeClasses = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg'
  };
  
  const baseClasses = 'relative overflow-hidden !font-heading font-medium rounded-full transition-all duration-300 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2 isolate group focus:outline-none focus:ring-2 focus:ring-primary-light/50 dark:focus:ring-primary-dark/50 focus:ring-offset-2 focus:ring-offset-background-light dark:focus:ring-offset-background-dark';
  
  const variantClasses = {
    primary: `
      btn-primary !font-heading
      bg-primary-light hover:bg-primary-hover-light text-white 
      shadow-button-light dark:bg-primary-dark dark:hover:bg-primary-hover-dark 
      dark:shadow-button-dark
      before:content-[''] before:absolute before:inset-0 before:rounded-full
      before:bg-gradient-to-r before:from-primary-light before:via-secondary-light before:to-primary-light
      dark:before:from-primary-dark dark:before:via-secondary-dark dark:to-primary-dark
      before:opacity-0 hover:before:opacity-10 before:transition-opacity before:duration-300 before:-z-10
    `,
    secondary: `
      btn-secondary !font-heading
      bg-surface-light dark:bg-background-dark/50 backdrop-blur-md
      border-2 border-border-light dark:border-border-dark
      text-text-primary-light dark:text-text-primary-dark
      shadow-sm
      hover:bg-primary-light/5 dark:hover:bg-primary-dark/5
      hover:border-primary-light dark:hover:border-primary-dark
      before:content-[''] before:absolute before:inset-0 before:rounded-full
      before:bg-gradient-to-r before:from-primary-light before:via-secondary-light before:to-primary-light
      dark:before:from-primary-dark dark:before:via-secondary-dark dark:to-primary-dark
      before:opacity-0 hover:before:opacity-5 before:transition-opacity before:duration-300 before:-z-10
    `,
    outline: `
      btn-outline !font-heading
      bg-transparent
      border border-border-light dark:border-border-dark
      text-text-secondary-light dark:text-text-secondary-dark
      hover:border-primary-light dark:hover:border-primary-dark
      hover:text-primary-light dark:hover:text-primary-dark
      transition-colors duration-300
    `,
    danger: `
      btn-danger !font-heading
      bg-red-500 hover:bg-red-600 text-white 
      shadow-button-light dark:bg-red-600 dark:hover:bg-red-700 
      dark:shadow-button-dark
      before:content-[''] before:absolute before:inset-0 before:rounded-full
      before:bg-gradient-to-r before:from-red-500 before:via-red-400 before:to-red-500
      dark:before:from-red-600 dark:before:via-red-500 dark:before:to-red-600
      before:opacity-0 hover:before:opacity-10 before:transition-opacity before:duration-300 before:-z-10
    `
  };

  const isDisabled = loading || disabled;
  
  // Detect if device is mobile/touch
  const isTouchDevice = () => {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  };
  
  // Force animation to continue on mobile
  useEffect(() => {
    if (loading && loaderRef.current) {
      const loader = loaderRef.current;
      // Force animation with inline styles that can't be overridden
      loader.style.setProperty('animation', 'spin 1s linear infinite', 'important');
      loader.style.setProperty('animation-play-state', 'running', 'important');
      
      // Re-apply animation if it gets interrupted
      const interval = setInterval(() => {
        if (loader && loading) {
          loader.style.setProperty('animation', 'spin 1s linear infinite', 'important');
          loader.style.setProperty('animation-play-state', 'running', 'important');
        }
      }, 100);
      
      return () => clearInterval(interval);
    }
  }, [loading]);
  
  // Handle click with analytics tracking and mobile focus fix
  const handleClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    // Only track if not disabled and tracking is enabled
    if (!isDisabled && !trackingDisabled) {
      // Use provided tracking ID or fallback to button text content
      const buttonName = trackingId || (typeof children === 'string' ? children : 'Button');
      // Use provided location or fallback to document title
      const buttonLocation = trackingLocation || document.title || 'Unknown';
      
      // Track the button click
      trackButtonClick(buttonName, buttonLocation);
    }
    
    // On mobile/touch devices, blur the button immediately to prevent focus interference
    if (isTouchDevice() && buttonRef.current) {
      buttonRef.current.blur();
    }
    
    // Call the original onClick handler if provided
    if (onClick) {
      onClick(e);
    }
  }, [children, isDisabled, onClick, trackingDisabled, trackingId, trackingLocation]);
  
  return (
    <button
      ref={buttonRef}
      className={`
        ${baseClasses}
        ${variantClasses[variant]} 
        ${sizeClasses[size]} 
        ${fullWidth ? 'w-full' : ''} 
        ${isDisabled ? 'cursor-not-allowed opacity-70' : ''}
        !font-heading
        ${className}
      `}
      disabled={isDisabled}
      onClick={handleClick}
      {...rest}
    >
      <span className="absolute inset-0 rounded-full overflow-hidden">
        <span className="absolute inset-0 w-[100%] bg-gradient-to-r from-transparent via-white to-transparent opacity-0 -translate-x-full group-hover:translate-x-full group-hover:opacity-20 transition-all duration-1000 ease-out"></span>
      </span>
      
      {loading ? (
        <div 
          ref={loaderRef}
          className="w-5 h-5 mr-2 animate-spin"
          style={{
            animation: 'spin 1s linear infinite !important',
            animationPlayState: 'running !important',
            willChange: 'transform'
          }}
        >
          <Loader2 className="w-full h-full" />
        </div>
      ) : leftIcon && (
        <span className="inline-flex">{leftIcon}</span>
      )}
      <span className="relative z-10 !font-heading">{children}</span>
      {!loading && rightIcon && <span className="inline-flex">{rightIcon}</span>}
    </button>
  );
} 